import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  getInvitationByToken,
  markInvitationOpened,
  markInvitationInProgress,
  submitResponse,
} from '../services/reviewInvitationService';
import { OPTICS_FRAMEWORK_2026 } from '../types/optics2026';
import {
  REVIEWER_ROLE_LABELS,
  REVIEWER_ATTRIBUTION,
  type ReviewInvitation,
  type ReviewResponseEntry,
  type SelfNarrative,
} from '../types/magicLink';
import OpticsScoringForm from '../components/review/OpticsScoringForm';
import ConfirmReviewSheet from '../components/review/ConfirmReviewSheet';
import CenterMessage from '../components/ui/CenterMessage';
import Button from '../components/ui/Button';
import { useToast } from '../components/ui/Toast';
import { useAutoSave } from '../hooks/useAutoSave';
import { useAnnounce } from '../hooks/useAnnounce';
import logoSvg from '../assets/logo.svg';

/**
 * Magic-link reviewer form (`/r/:token`) — public, no-login.
 *
 * Thin parent: owns invitation, localStorage draft, submit logic, terminal
 * states, and the surrounding chrome (privacy banner + onboarding intro +
 * ConfirmReviewSheet overlay). Scoring UI delegated to <OpticsScoringForm>.
 *
 * Audit fixes applied:
 *   #1  Dedup — no longer inlines the scoring UI; uses OpticsScoringForm
 *   #2  ConfirmReviewSheet shown before final submit
 *   #3  Validation errors use area names, not codes
 *   #6  Privacy banner pinned at top, not buried in footer
 *   #8  Real auto-save label via useAutoSave hook
 *   #11 Rascal logo in context banner (brand presence on public surface)
 *   #15 Loading state has Material Symbol spinner + bigger touch surface
 *   #19 First-visit onboarding intro card with "Got it, start review" CTA
 *   #27 Screen-reader announcements on state changes (useAnnounce)
 */

type FormState =
  | 'loading'
  | 'onboarding'
  | 'form'
  | 'submitting'
  | 'submitted'
  | 'invalid'
  | 'expired'
  | 'already_done';

const draftKey = (token: string) => `epr-draft-${token}`;
const onboardKey = (token: string) => `epr-onboard-${token}`;

interface DraftShape {
  entries: Record<string, ReviewResponseEntry>;
  selfNarrative: SelfNarrative;
  updatedAt: string;
}

const formatExpiry = (raw: unknown): Date | null => {
  if (!raw) return null;
  const obj = raw as { toDate?: () => Date; seconds?: number };
  if (typeof obj.toDate === 'function') return obj.toDate();
  if (typeof obj.seconds === 'number') return new Date(obj.seconds * 1000);
  return null;
};

const getAreaName = (areaId: string): string => {
  for (const v of OPTICS_FRAMEWORK_2026) {
    for (const a of v.areas) {
      if (a.id === areaId) return `${v.title} → ${a.title}`;
    }
  }
  return areaId;
};

const MagicLinkReviewForm: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<FormState>('loading');
  const [invitation, setInvitation] = useState<ReviewInvitation | null>(null);
  const [errorMsg, setErrorMsg] = useState('');

  const [entries, setEntries] = useState<Record<string, ReviewResponseEntry>>({});
  const [selfNarrative, setSelfNarrative] = useState<SelfNarrative>({});

  const [confirmOpen, setConfirmOpen] = useState(false);

  const inProgressMarked = useRef(false);
  const dirty = useRef(false);

  const save = useAutoSave();
  const announce = useAnnounce();
  const toast = useToast();

  // ─── Init: load invitation, restore draft ──────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        if (!token) {
          setState('invalid');
          return;
        }

        // No auth needed — token IS the doc ID.
        const inv = await getInvitationByToken(token);
        if (cancelled) return;
        if (!inv) {
          setState('invalid');
          return;
        }
        if (inv.status === 'submitted') {
          setState('already_done');
          return;
        }
        if (inv.status === 'expired') {
          setState('expired');
          return;
        }
        const expiresAt = formatExpiry(inv.expiresAt);
        if (expiresAt && expiresAt < new Date()) {
          setState('expired');
          return;
        }
        setInvitation(inv);

        // Restore draft if any
        let hadDraft = false;
        try {
          const raw = localStorage.getItem(draftKey(token));
          if (raw) {
            const draft = JSON.parse(raw) as DraftShape;
            if (draft.entries) setEntries(draft.entries);
            if (draft.selfNarrative) setSelfNarrative(draft.selfNarrative);
            hadDraft = Object.keys(draft.entries ?? {}).length > 0;
          }
        } catch {
          /* corrupt draft — fall through */
        }

        // Onboarding shown only on the very first visit per token, but skip
        // if reviewer has already started (resume case).
        const onboarded = localStorage.getItem(onboardKey(token)) === 'done';
        setState(onboarded || hadDraft ? 'form' : 'onboarding');

        // Mark opened (background, no await)
        if (inv.status === 'sent') void markInvitationOpened(inv.id);
      } catch (e) {
        if (cancelled) return;
        setErrorMsg(String(e));
        setState('invalid');
      }
    };
    void init();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // ─── Auto-save draft (debounced via save state machine) ────────────────
  useEffect(() => {
    if (state !== 'form' || !token) return;
    // First effect run on mount with empty entries — skip until user edits
    if (!dirty.current) return;

    save.markSaving();
    try {
      const draft: DraftShape = {
        entries,
        selfNarrative,
        updatedAt: new Date().toISOString(),
      };
      localStorage.setItem(draftKey(token), JSON.stringify(draft));
      // localStorage is synchronous — flip to saved immediately, but defer
      // so the user sees a brief "Saving…" then "Saved just now"
      window.setTimeout(() => save.markSaved(), 250);
    } catch (e) {
      save.markError((e as Error).message ?? 'Storage full?');
    }
  }, [entries, selfNarrative, state, token, save]);

  // Mark dirty + in-progress on first user interaction
  useEffect(() => {
    if (!invitation || inProgressMarked.current) return;
    if (Object.keys(entries).length === 0) return;
    dirty.current = true;
    inProgressMarked.current = true;
    void markInvitationInProgress(invitation.id);
  }, [entries, invitation]);

  // Audit #17 — beforeunload guard when reviewer has unsaved progress
  useEffect(() => {
    if (state !== 'form' || Object.keys(entries).length === 0) return;
    const handler = (e: BeforeUnloadEvent) => {
      // Modern browsers ignore custom messages — preventDefault triggers their
      // generic "Reload site?" dialog. Draft IS saved to localStorage so
      // this is a soft guard, not a hard one.
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [state, entries]);

  // ─── Submit ────────────────────────────────────────────────────────────
  const requestSubmit = () => {
    if (!invitation) return;
    // Soft validation — at least one score per area
    const incompleteAreas: string[] = [];
    for (const v of OPTICS_FRAMEWORK_2026) {
      for (const a of v.areas) {
        const e = entries[a.id];
        const done =
          e &&
          ['ind', 'team', 'co'].every(
            l => typeof e.scores[l as 'ind' | 'team' | 'co'] === 'number' &&
              (e.scores[l as 'ind' | 'team' | 'co'] as number) > 0,
          );
        if (!done) incompleteAreas.push(getAreaName(a.id));
      }
    }
    if (incompleteAreas.length) {
      // Audit #3 — human-readable names instead of codes
      const list = incompleteAreas.slice(0, 3).join('; ');
      const more =
        incompleteAreas.length > 3
          ? ` (and ${incompleteAreas.length - 3} more)`
          : '';
      setErrorMsg(`Some areas still need a score: ${list}${more}.`);
      announce('Some scores are missing. Please complete all sections.');
      return;
    }
    setErrorMsg('');
    setConfirmOpen(true);
  };

  const handleConfirmSubmit = async () => {
    if (!invitation) return;
    setState('submitting');
    setErrorMsg('');
    try {
      await submitResponse({
        invitation,
        entries: Object.values(entries),
        selfNarrative: invitation.reviewerRole === 'self' ? selfNarrative : undefined,
      });
      if (token) {
        localStorage.removeItem(draftKey(token));
        localStorage.removeItem(onboardKey(token));
      }
      setConfirmOpen(false);
      setState('submitted');
      announce('Review submitted. Thank you.');
    } catch (e) {
      const msg = (e as Error).message ?? String(e);
      setErrorMsg(msg);
      setState('form');
      announce('Submission failed. Please try again.');
      // Audit #9 — Toast surfaces the error even when ConfirmSheet closes and
      // user is back at scroll position above the inline error block
      toast.show('Submission failed — please try again', { icon: 'error', duration: 4000 });
    }
  };

  const dismissOnboarding = () => {
    if (token) localStorage.setItem(onboardKey(token), 'done');
    setState('form');
  };

  // ─── Render: terminal states ───────────────────────────────────────────
  if (state === 'loading') {
    return (
      <CenterMessage
        tone="info"
        title="Loading review…"
        body="Verifying your link. This takes a second."
      />
    );
  }
  if (state === 'invalid') {
    return (
      <CenterMessage
        tone="error"
        title="Link not valid"
        body={
          errorMsg ||
          'This review link is not recognised. Check that you copied the full URL, or contact HR.'
        }
      />
    );
  }
  if (state === 'expired') {
    return (
      <CenterMessage
        tone="warning"
        title="Link expired"
        body="This review link has expired. Please request a new link from HR."
      />
    );
  }
  if (state === 'already_done') {
    return (
      <CenterMessage
        tone="success"
        title="Already submitted"
        body="This review has already been submitted. Thank you."
      />
    );
  }
  if (state === 'submitted' && invitation) {
    const isAttributed = REVIEWER_ATTRIBUTION[invitation.reviewerRole] === 'attributed';
    return (
      <CenterMessage
        tone="success"
        title="Review submitted"
        body={
          <>
            Thank you. Your feedback for <b>{invitation.revieweeName}</b> is locked in.
            <br />
            {isAttributed
              ? 'Your name will be visible to HR and the line manager.'
              : 'Your individual response is kept confidential — only the aggregated result is shared.'}
          </>
        }
      />
    );
  }

  // ─── Render: onboarding intro (first visit only) ───────────────────────
  if (state === 'onboarding' && invitation) {
    return <Onboarding invitation={invitation} onStart={dismissOnboarding} />;
  }

  // ─── Render: form ──────────────────────────────────────────────────────
  if (!invitation) return null;
  const isSelf = invitation.reviewerRole === 'self';
  const isAttributed = REVIEWER_ATTRIBUTION[invitation.reviewerRole] === 'attributed';
  const reviewerRoleLabel = REVIEWER_ROLE_LABELS[invitation.reviewerRole];

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Sticky privacy banner — audit #6 */}
      <PrivacyBanner isAttributed={isAttributed} />

      <div className="max-w-3xl mx-auto px-4 py-5 md:px-6 md:py-6">
        {/* Context card */}
        <div className="bg-white border border-stone-200 rounded-2xl p-5 md:p-6 mb-3 md:mb-4">
          <div className="flex justify-between items-start gap-4">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-[#011132] flex items-center justify-center shrink-0">
                <img src={logoSvg} alt="" aria-hidden="true" className="w-7 h-7 object-contain" />
              </div>
              <div className="min-w-0">
                <div className="text-xs text-stone-500 uppercase tracking-wider mb-1">
                  You're reviewing
                </div>
                <div className="text-xl font-semibold text-stone-900 tracking-tight truncate">
                  {invitation.revieweeName}
                </div>
                <div className="text-sm text-stone-600 mt-0.5">
                  Role: <b>{reviewerRoleLabel}</b>
                  {' · '}OPTICS 2026
                </div>
              </div>
            </div>
            <span className="hidden sm:inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full shrink-0">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" aria-hidden="true" />
              Verified link
            </span>
          </div>
        </div>

        <OpticsScoringForm
          entries={entries}
          setEntries={setEntries}
          selfNarrative={selfNarrative}
          setSelfNarrative={setSelfNarrative}
          isSelf={isSelf}
          onSubmit={requestSubmit}
          submitting={state === 'submitting'}
          submitLabel="Review your answers"
          errorMsg={errorMsg}
          autoSaveLabel={save.label}
        />

        <p className="text-sm text-stone-500 text-center mt-6 mb-4 leading-relaxed">
          {isAttributed ? 'Attributed review' : 'Anonymous in admin reports'} · Confidential — internal use only
        </p>
      </div>

      {/* Confirm sheet — audit #2 */}
      <ConfirmReviewSheet
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmSubmit}
        entries={entries}
        selfNarrative={selfNarrative}
        isSelf={isSelf}
        isAttributed={isAttributed}
        revieweeName={invitation.revieweeName}
        reviewerRoleLabel={reviewerRoleLabel}
        submitting={state === 'submitting'}
        submitLabel="Submit final review"
      />
    </div>
  );
};

// ─── PrivacyBanner ───────────────────────────────────────────────────────

const PrivacyBanner: React.FC<{ isAttributed: boolean }> = ({ isAttributed }) => (
  <div
    className={[
      'sticky top-0 z-20 backdrop-blur',
      isAttributed
        ? 'bg-blue-50/95 border-b border-blue-200 text-blue-900'
        : 'bg-emerald-50/95 border-b border-emerald-200 text-emerald-900',
    ].join(' ')}
    role="status"
  >
    <div className="max-w-3xl mx-auto px-4 h-11 flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 min-w-0">
        <span
          className="material-symbols-rounded shrink-0"
          style={{ fontSize: 18 }}
          aria-hidden="true"
        >
          {isAttributed ? 'badge' : 'lock'}
        </span>
        <span className="text-sm font-medium truncate">
          {isAttributed
            ? 'Attributed review — your name will be visible to HR & line manager'
            : 'Anonymous — only the aggregated score is shared with HR'}
        </span>
      </div>
      <span className="hidden sm:inline-flex items-center gap-1 text-xs shrink-0">
        <span
          className={[
            'w-1.5 h-1.5 rounded-full',
            isAttributed ? 'bg-blue-500' : 'bg-emerald-500',
          ].join(' ')}
          aria-hidden="true"
        />
        Verified link
      </span>
    </div>
  </div>
);

// ─── Onboarding intro card (audit #19) ────────────────────────────────────

const Onboarding: React.FC<{
  invitation: ReviewInvitation;
  onStart: () => void;
}> = ({ invitation, onStart }) => {
  const isSelf = invitation.reviewerRole === 'self';
  const isAttributed = REVIEWER_ATTRIBUTION[invitation.reviewerRole] === 'attributed';
  const reviewerRoleLabel = REVIEWER_ROLE_LABELS[invitation.reviewerRole];

  const headlineRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headlineRef.current?.focus();
  }, []);

  const cards = useMemo(
    () => [
      {
        icon: 'schedule',
        title: 'About 25 minutes',
        body: 'Six OPTICS sections × three contribution lenses. Auto-saved as you go — close and resume any time.',
      },
      {
        icon: isAttributed ? 'badge' : 'lock',
        title: isAttributed ? 'Attributed review' : 'Anonymous in reports',
        body: isAttributed
          ? 'Your name will be visible to HR and the line manager.'
          : 'HR sees only the aggregated average. Your individual answers and identity stay private.',
      },
      {
        icon: 'check_circle',
        title: 'Submit when ready',
        body:
          'Scores are required. Evidence + development action are optional — leave them blank if nothing comes to mind.',
      },
    ],
    [isAttributed],
  );

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full bg-white border border-stone-200 rounded-2xl p-6 md:p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-[#011132] flex items-center justify-center shrink-0">
            <img src={logoSvg} alt="" aria-hidden="true" className="w-8 h-8 object-contain" />
          </div>
          <div className="min-w-0">
            <div className="text-xs text-stone-500 uppercase tracking-wider">Rascal EPR</div>
            <div className="text-base font-semibold text-stone-900">Employee Performance Review</div>
          </div>
        </div>

        <h1
          ref={headlineRef}
          tabIndex={-1}
          className="text-2xl font-semibold text-stone-900 tracking-tight focus:outline-none"
        >
          {isSelf ? 'Time for your self-review' : `You've been asked to review ${invitation.revieweeName}`}
        </h1>
        <p className="text-base text-stone-600 mt-2 leading-relaxed">
          {isSelf
            ? 'Reflect on your year across the six OPTICS values.'
            : `As ${reviewerRoleLabel.toLowerCase()}, your perspective matters. Here's what to expect.`}
        </p>

        <ul className="mt-5 space-y-3">
          {cards.map(c => (
            <li key={c.title} className="flex items-start gap-3">
              <span
                className="material-symbols-rounded text-stone-500 shrink-0 mt-0.5"
                style={{ fontSize: 22 }}
                aria-hidden="true"
              >
                {c.icon}
              </span>
              <div className="min-w-0">
                <div className="text-base font-medium text-stone-900">{c.title}</div>
                <div className="text-sm text-stone-600 leading-relaxed">{c.body}</div>
              </div>
            </li>
          ))}
        </ul>

        <Button
          variant="primary"
          fullWidth
          icon="arrow_forward"
          iconPosition="trailing"
          onClick={onStart}
          className="mt-6"
        >
          Got it, start the review
        </Button>
      </div>
    </div>
  );
};

export default MagicLinkReviewForm;
